import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  MapPin, Building2, Wifi, Plug, Volume2, Car, Users, 
  Coffee, Clock, Mail, CheckCircle2, ChevronRight, ChevronLeft,
  Loader2, Locate, UtensilsCrossed, Home, Phone, Sun, Wind, Leaf, Dog, 
  Accessibility, Monitor, User, FileText, Sparkles, Check, Camera, X, Upload, Image as ImageIcon,
  Trophy, Star, LogIn, Lock, Printer
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import AddressAutocomplete from "../components/AddressAutocomplete";
import PriceRating from "../components/PriceRating";
import SEO from "../components/SEO";
import WiFiSpeedTest from "../components/WiFiSpeedTest";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const venueTypes = [
  { value: "cafe", label: "Cafe", icon: Coffee },
  { value: "pub", label: "Pub", icon: Building2 },
  { value: "hotel", label: "Hotel", icon: Home },
  { value: "library", label: "Library", icon: Building2 },
  { value: "hub", label: "Hub / Coworking", icon: Users },
  { value: "other", label: "Other", icon: Building2 },
];

const wifiOptions = [
  { value: "very_fast", label: "Very Fast", desc: "100+ Mbps" },
  { value: "fast", label: "Fast", desc: "50+ Mbps" },
  { value: "good", label: "Good", desc: "20+ Mbps" },
  { value: "poor", label: "Poor", desc: "< 20 Mbps" },
  { value: "none", label: "None", desc: "No WiFi" }
];

const noiseOptions = [
  { value: "quiet", label: "Quiet", desc: "Library-like" },
  { value: "moderate", label: "Moderate", desc: "Background chatter" },
  { value: "lively", label: "Lively", desc: "Energetic atmosphere" }
];

const workFeatures = [
  { key: "long_stay_friendly", label: "Long Stays OK", icon: Clock },
  { key: "good_for_calls", label: "Good for Calls", icon: Phone },
  { key: "natural_light", label: "Natural Light", icon: Sun },
  { key: "has_ac", label: "Air Con", icon: Wind },
  { key: "is_spacious", label: "Spacious", icon: Users },
  { key: "has_outdoor_seating", label: "Outdoor", icon: Leaf },
  { key: "group_friendly", label: "Group OK", icon: Users },
  { key: "has_standing_desks", label: "Standing Desks", icon: Monitor },
  { key: "has_meeting_rooms", label: "Meeting Rooms", icon: Users },
  { key: "has_parking", label: "Parking", icon: Car },
  { key: "pet_friendly", label: "Pet Friendly", icon: Dog },
  { key: "wheelchair_accessible", label: "Accessible", icon: Accessibility },
  { key: "has_printing", label: "Printing", icon: Printer },
];

const STEPS = [
  { id: 1, title: "Location", icon: MapPin },
  { id: 2, title: "Work Setup", icon: Wifi },
  { id: 3, title: "Your Details", icon: User },
  { id: 4, title: "Extras", icon: Sparkles },
];

export default function AddLocationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Contributor auth state
  const [contributor, setContributor] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [authError, setAuthError] = useState("");
  
  // Check for existing contributor session and restore form data on mount
  // Also handles incoming contributor data from Google Auth callback
  useEffect(() => {
    // First, check if we got contributor from auth callback redirect
    const contributorFromState = location.state?.contributor;
    
    let contributorData = null;
    
    if (contributorFromState) {
      // Came from auth callback - use the contributor data passed via state
      contributorData = contributorFromState;
      setContributor(contributorData);
      sessionStorage.setItem("contributor", JSON.stringify(contributorData));
      
      // Clear the state to prevent re-processing on future renders
      window.history.replaceState({}, '', window.location.pathname);
    } else {
      // Check sessionStorage for existing session
      const storedContributor = sessionStorage.getItem("contributor");
      if (storedContributor) {
        try {
          contributorData = JSON.parse(storedContributor);
          setContributor(contributorData);
        } catch (e) {
          sessionStorage.removeItem("contributor");
        }
      }
    }
    
    // If we have a contributor, restore saved form data and auto-fill name/email
    if (contributorData) {
      // Restore saved form data first
      const savedFormData = sessionStorage.getItem("addVenueFormData");
      const savedStep = sessionStorage.getItem("addVenueCurrentStep");
      const savedImages = sessionStorage.getItem("addVenueImages");
      
      if (savedFormData) {
        try {
          const parsed = JSON.parse(savedFormData);
          // Auto-fill contributor details from the contributor profile
          setFormData(prev => ({ 
            ...prev, 
            ...parsed,
            submitter_name: contributorData.name || contributorData.username || parsed.submitter_name || "",
            submitter_email: contributorData.email || parsed.submitter_email || ""
          }));
        } catch (e) {
          // If no saved form, just auto-fill name/email
          setFormData(prev => ({
            ...prev,
            submitter_name: contributorData.name || contributorData.username || "",
            submitter_email: contributorData.email || ""
          }));
        }
      } else {
        // No saved form, just auto-fill name/email
        setFormData(prev => ({
          ...prev,
          submitter_name: contributorData.name || contributorData.username || "",
          submitter_email: contributorData.email || ""
        }));
      }
      
      if (savedStep) {
        const step = parseInt(savedStep) || 1;
        setCurrentStep(step);
      }
      
      if (savedImages) {
        try {
          setUploadedImages(JSON.parse(savedImages));
        } catch (e) {
          // Ignore
        }
      }
      
      // Clean up saved data after restoring
      sessionStorage.removeItem("addVenueFormData");
      sessionStorage.removeItem("addVenueCurrentStep");
      sessionStorage.removeItem("addVenueImages");
    }
  }, [location.state]);
  
  // Handle page refresh case - restore form data even without contributor
  // (First useEffect handles contributor-related restoration)
  useEffect(() => {
    // Skip if we already have a contributor (handled by first useEffect)
    if (contributor) return;
    
    // Check if there's any saved form data to restore (from page refresh without auth)
    const savedFormData = sessionStorage.getItem("addVenueFormData");
    const savedStep = sessionStorage.getItem("addVenueCurrentStep");
    const savedImages = sessionStorage.getItem("addVenueImages");
    
    if (savedFormData || savedStep || savedImages) {
      if (savedFormData) {
        try {
          const parsed = JSON.parse(savedFormData);
          setFormData(prev => ({ ...prev, ...parsed }));
        } catch (e) {
          sessionStorage.removeItem("addVenueFormData");
        }
      }
      
      if (savedStep) {
        setCurrentStep(parseInt(savedStep) || 1);
      }
      
      if (savedImages) {
        try {
          setUploadedImages(JSON.parse(savedImages));
        } catch (e) {
          // Ignore
        }
      }
    }
  }, [contributor]);
  
  const handleGoogleLogin = () => {
    // Save current form state before redirecting to Google Auth
    sessionStorage.setItem("addVenueFormData", JSON.stringify(formData));
    sessionStorage.setItem("addVenueCurrentStep", currentStep.toString());
    if (uploadedImages.length > 0) {
      sessionStorage.setItem("addVenueImages", JSON.stringify(uploadedImages));
    }
    
    // Store that we came from add-location page
    sessionStorage.setItem('auth_return_url', '/add-location');
    sessionStorage.setItem('add_location_auth_pending', 'true');
    
    // Use the standard auth callback route - it will redirect back to /add-location
    const redirectUrl = window.location.origin + '/contributor/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  
  const handleGoogleCallback = async (email, name, profilePicture) => {
    setAuthLoading(true);
    try {
      // Check if contributor exists
      let response;
      try {
        response = await axios.get(`${API}/contributors/by-email/${encodeURIComponent(email)}`);
      } catch (err) {
        if (err.response?.status === 404) {
          // Create new contributor via Google
          response = await axios.post(`${API}/contributors`, {
            email: email,
            name: name || email.split('@')[0],
            password: null, // Google auth, no password
            auth_provider: "google",
            avatar_url: profilePicture  // Store Google profile picture
          });
        } else {
          throw err;
        }
      }
      
      const contributorData = response.data;
      // Add profile picture if not already in data
      if (profilePicture && !contributorData.avatar_url) {
        contributorData.avatar_url = profilePicture;
      }
      setContributor(contributorData);
      sessionStorage.setItem("contributor", JSON.stringify(contributorData));
      
      // Auto-fill form
      setFormData(prev => ({
        ...prev,
        submitter_name: contributorData.name || name || "",
        submitter_email: contributorData.email || email
      }));
      
      toast.success(`Welcome back, ${contributorData.name || name}!`);
    } catch (err) {
      toast.error("Failed to sign in with Google");
    } finally {
      setAuthLoading(false);
    }
  };
  
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail.trim()) {
      setAuthError("Please enter your email");
      return;
    }
    
    setAuthLoading(true);
    setAuthError("");
    
    try {
      const response = await axios.post(`${API}/contributors/login`, {
        email: loginEmail.trim(),
        password: loginPassword
      });
      
      const contributorData = response.data;
      setContributor(contributorData);
      sessionStorage.setItem("contributor", JSON.stringify(contributorData));
      
      // Auto-fill form
      setFormData(prev => ({
        ...prev,
        submitter_name: contributorData.name || "",
        submitter_email: contributorData.email || loginEmail
      }));
      
      setShowEmailLogin(false);
      toast.success(`Welcome back, ${contributorData.name}!`);
    } catch (err) {
      if (err.response?.status === 404) {
        setAuthError("Account not found. Please sign up first.");
      } else if (err.response?.status === 401) {
        setAuthError("Invalid password");
      } else {
        setAuthError(err.response?.data?.detail || "Login failed");
      }
    } finally {
      setAuthLoading(false);
    }
  };
  
  const handleLogout = () => {
    setContributor(null);
    sessionStorage.removeItem("contributor");
    setFormData(prev => ({
      ...prev,
      submitter_name: "",
      submitter_email: ""
    }));
    toast.success("Logged out");
  };
  
  const [formData, setFormData] = useState({
    // Step 1: Location (Required)
    name: "",
    venue_type: "cafe",
    other_venue_description: "",  // For "other" venue type
    address: "",
    city: "",
    postcode: "",
    
    // Step 2: Work Setup (Required)
    wifi_quality: "good",
    noise_level: "moderate",
    power_outlets: 5,
    has_food: true,
    has_drinks: true,
    description: "",
    
    // WiFi Speed Test Results
    wifi_speed_mbps: null,
    wifi_upload_mbps: null,
    wifi_latency_ms: null,
    wifi_speed_tier: null,
    plug_sockets_count: null,
    
    // Step 3: Your Details (Required)
    submitter_name: "",
    submitter_email: "",
    notes: "",
    agreed_to_terms: false,
    marketing_opt_out: false,
    
    // Step 4: Extras (Optional)
    neighborhood: "",
    opening_hours: "",
    coffee_avg: "",
    food_avg: "",
    // Work-Friendly Features (all optional)
    long_stay_friendly: false,
    good_for_calls: false,
    has_outdoor_seating: false,
    has_ac: false,
    is_spacious: false,
    natural_light: false,
    pet_friendly: false,
    wheelchair_accessible: false,
    group_friendly: false,
    has_standing_desks: false,
    has_meeting_rooms: false,
    has_parking: false,
    has_printing: false,
    printing_cost_bw: "",
    printing_cost_color: "",
    // Optional Review (contributor only)
    include_review: false,
    review_rating: 7,
    review_comment: "",
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Compress and resize image using Canvas API
  const compressImage = (file, maxDimension = 1200, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      const reader = new FileReader();
      
      reader.onload = (e) => {
        img.src = e.target.result;
      };
      
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }
        
        // Create canvas and draw resized image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to JPEG with compression
        const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        const base64 = compressedDataUrl.split(',')[1];
        
        resolve({
          base64,
          preview: compressedDataUrl,
          originalSize: file.size,
          compressedSize: Math.round((base64.length * 3) / 4) // Approximate size
        });
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    // Limit to 3 images for submissions
    if (uploadedImages.length + files.length > 3) {
      toast.error("Maximum 3 photos allowed per submission");
      return;
    }
    
    setUploadingImage(true);
    
    for (const file of files) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        continue;
      }
      
      // Accept images up to 8MB (will be compressed)
      if (file.size > 8 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 8MB limit`);
        continue;
      }
      
      try {
        // Compress and resize the image to max 2MB
        let compressed = await compressImage(file, 1600, 0.85);
        
        // If still too large, try more aggressive compression
        if (compressed.compressedSize > 2 * 1024 * 1024) {
          compressed = await compressImage(file, 1200, 0.7);
        }
        
        // Final check - reject if still too large
        if (compressed.compressedSize > 2 * 1024 * 1024) {
          toast.error(`${file.name} could not be compressed to under 2MB. Please use a smaller image.`);
          continue;
        }
        
        // Calculate compression ratio for feedback
        const savedPercent = Math.round((1 - compressed.compressedSize / file.size) * 100);
        
        setUploadedImages(prev => [...prev, { 
          base64: compressed.base64, 
          preview: compressed.preview, 
          name: file.name.replace(/\.[^/.]+$/, '.jpg'), // Change extension to .jpg
          type: 'image/jpeg',
          originalSize: file.size,
          compressedSize: compressed.compressedSize
        }]);
        
        // Show compression info for large files
        if (file.size > 500 * 1024 && savedPercent > 20) {
          toast.success(`Compressed ${file.name} by ${savedPercent}%`);
        }
      } catch {
        toast.error(`Failed to process ${file.name}`);
      }
    }
    
    setUploadingImage(false);
    e.target.value = ''; // Reset input
  };

  const removeImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddressSelect = (addressData) => {
    setFormData(prev => ({
      ...prev,
      address: addressData.address || "",
      city: addressData.city || prev.city,
      postcode: addressData.postcode || prev.postcode,
      neighborhood: addressData.neighborhood || prev.neighborhood,
      latitude: addressData.lat,
      longitude: addressData.lng
    }));
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported");
      return;
    }
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
          );
          const data = await response.json();
          const addr = data.address || {};
          setFormData(prev => ({
            ...prev,
            address: [addr.road, addr.house_number].filter(Boolean).join(" ") || data.display_name?.split(",")[0] || "",
            city: addr.city || addr.town || addr.village || "",
            postcode: addr.postcode || "",
            neighborhood: addr.suburb || addr.neighbourhood || "",
            latitude,
            longitude
          }));
          toast.success("Location detected!");
        } catch {
          toast.error("Couldn't get address");
        } finally {
          setGettingLocation(false);
        }
      },
      () => {
        toast.error("Location access denied");
        setGettingLocation(false);
      }
    );
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.name.trim()) {
          toast.error("Please enter the venue name");
          return false;
        }
        if (!formData.city.trim()) {
          toast.error("Please enter the city");
          return false;
        }
        return true;
      case 2:
        if (!formData.description.trim()) {
          toast.error("Please add a brief description");
          return false;
        }
        return true;
      case 3:
        if (!formData.submitter_name.trim()) {
          toast.error("Please enter your name");
          return false;
        }
        if (!formData.submitter_email.trim()) {
          toast.error("Please enter your email");
          return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.submitter_email.trim())) {
          toast.error("Please enter a valid email");
          return false;
        }
        if (!formData.agreed_to_terms) {
          toast.error("Please agree to the Terms & Conditions");
          return false;
        }
        return true;
      case 4:
        return true; // All optional
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      if (currentStep < 4) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return; // Re-validate required fields
    
    setSubmitting(true);
    try {
      // Build description - use other_venue_description if "other" type is selected
      let autoDescription = formData.description.trim();
      if (!autoDescription) {
        if (formData.venue_type === "other" && formData.other_venue_description) {
          autoDescription = `A ${formData.other_venue_description} in ${formData.city} with ${formData.wifi_quality} WiFi.`;
        } else {
          autoDescription = `A ${formData.venue_type} in ${formData.city} with ${formData.wifi_quality} WiFi.`;
        }
      }
      
      const submitData = {
        ...formData,
        submitter_name: formData.submitter_name.trim(),
        submitter_email: formData.submitter_email.trim(),
        description: autoDescription,
        coffee_avg: formData.coffee_avg ? String(formData.coffee_avg) : "",
        food_avg: formData.food_avg ? String(formData.food_avg) : "",
        marketing_opt_out: formData.marketing_opt_out,
        // Include uploaded images
        images: uploadedImages.map(img => ({
          data: img.base64,
          filename: img.name,
          content_type: img.type
        })),
        // Include review data if contributor wants to leave a review
        include_review: formData.include_review,
        review_data: formData.include_review ? {
          rating: formData.review_rating,
          comment: formData.review_comment.trim()
        } : null
      };
      
      await axios.post(`${API}/locations/submit`, submitData);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast.success("Location submitted!");
    } catch (error) {
      console.error("Submit error:", error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  // Success Screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SEO title="Submission Received" />
        <Header />
        <main className="max-w-xl mx-auto px-4 py-16">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl p-8 shadow-lg text-center"
          >
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Thank You!</h1>
            <p className="text-gray-600 mb-2">
              <strong>{formData.name}</strong> has been submitted for review.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              We'll notify you at <strong>{formData.submitter_email}</strong> once approved.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => {
                  setSubmitted(false);
                  setCurrentStep(1);
                  setFormData({
                    name: "", venue_type: "cafe", address: "", city: "", postcode: "",
                    wifi_quality: "good", noise_level: "moderate", power_outlets: 5,
                    has_food: true, has_drinks: true, description: "",
                    submitter_name: formData.submitter_name, submitter_email: formData.submitter_email,
                    notes: "", agreed_to_terms: false, marketing_opt_out: formData.marketing_opt_out,
                    neighborhood: "", opening_hours: "", coffee_avg: "", food_avg: "",
                    long_stay_friendly: false, good_for_calls: false, has_outdoor_seating: false,
                    has_ac: false, is_spacious: false, natural_light: false, pet_friendly: false,
                    wheelchair_accessible: false, group_friendly: false, has_standing_desks: false,
                    has_meeting_rooms: false, has_parking: false, has_printing: false,
                    printing_cost_bw: "", printing_cost_color: "",
                  });
                }}
                className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Add Another
              </button>
              <button
                onClick={() => navigate("/browse")}
                className="px-5 py-2.5 bg-brand text-white rounded-lg hover:bg-brand-dark"
              >
                Browse Venues
              </button>
            </div>
          </motion.div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative">
      {/* Strider Logo Watermark */}
      <div 
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-[0.025] pointer-events-none z-0"
        style={{
          width: '600px',
          height: '600px',
          backgroundImage: 'url(/strider-logo-bg.png)',
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      />
      
      <SEO title="Add a Location" description="Help fellow remote workers by adding a workspace." />
      <Header />
      
      <main className="max-w-2xl mx-auto px-4 py-8 relative z-10">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {STEPS.map((step, idx) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isComplete = currentStep > step.id;
              
              return (
                <div key={step.id} className="flex items-center flex-1">
                  <div className="flex flex-col items-center">
                    <div 
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                        isComplete ? 'bg-green-500 text-white' :
                        isActive ? 'bg-brand text-white ring-4 ring-brand/20' :
                        'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {isComplete ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                    </div>
                    <span className={`text-xs mt-1.5 font-medium ${
                      isActive ? 'text-brand' : isComplete ? 'text-green-600' : 'text-gray-400'
                    }`}>
                      {step.title}
                    </span>
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 ${
                      currentStep > step.id ? 'bg-green-500' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Form Container */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <AnimatePresence mode="wait">
            {/* Step 1: Location */}
            {currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-6"
              >
                <h2 className="text-xl font-bold text-gray-900 mb-1">Where is it?</h2>
                <p className="text-sm text-gray-500 mb-6">Tell us about the venue location</p>
                
                <div className="space-y-5">
                  {/* Venue Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Venue Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => updateField("name", e.target.value)}
                      placeholder="e.g., The Coffee House"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                      data-testid="venue-name-input"
                    />
                  </div>
                  
                  {/* Venue Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Venue Type <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                      {venueTypes.map(type => {
                        const Icon = type.icon;
                        return (
                          <button
                            key={type.value}
                            type="button"
                            onClick={() => updateField("venue_type", type.value)}
                            className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                              formData.venue_type === type.value
                                ? "border-brand bg-brand/5 text-brand"
                                : "border-gray-200 hover:border-gray-300 text-gray-600"
                            }`}
                          >
                            <Icon className="w-5 h-5" />
                            <span className="text-xs font-medium">{type.label}</span>
                          </button>
                        );
                      })}
                    </div>
                    
                    {/* Other venue type description */}
                    {formData.venue_type === "other" && (
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Describe the venue type <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.other_venue_description || ""}
                          onChange={(e) => updateField("other_venue_description", e.target.value)}
                          placeholder="e.g., Community center, Art gallery, Museum cafe..."
                          className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                        />
                        <p className="text-xs text-gray-400 mt-1">Help us understand what type of workspace this is</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Address */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-sm font-medium text-gray-700">
                        Address
                      </label>
                      <button
                        type="button"
                        onClick={getCurrentLocation}
                        disabled={gettingLocation}
                        className="text-xs text-brand flex items-center gap-1 hover:underline"
                      >
                        <Locate className="w-3 h-3" />
                        {gettingLocation ? "Detecting..." : "Use my location"}
                      </button>
                    </div>
                    <AddressAutocomplete
                      value={formData.address}
                      onChange={(val) => updateField("address", val)}
                      onSelect={handleAddressSelect}
                      placeholder="Start typing address..."
                    />
                  </div>
                  
                  {/* City & Postcode */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        City <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => updateField("city", e.target.value)}
                        placeholder="e.g., London"
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Postcode
                      </label>
                      <input
                        type="text"
                        value={formData.postcode}
                        onChange={(e) => updateField("postcode", e.target.value)}
                        placeholder="e.g., SW1A 1AA"
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: Work Setup */}
            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-6"
              >
                <h2 className="text-xl font-bold text-gray-900 mb-1">Work Essentials</h2>
                <p className="text-sm text-gray-500 mb-6">What makes it good for working?</p>
                
                <div className="space-y-5">
                  {/* WiFi Speed Test - New Feature */}
                  <WiFiSpeedTest 
                    onSpeedResult={(result) => {
                      updateField("wifi_speed_mbps", result.download_mbps);
                      updateField("wifi_upload_mbps", result.upload_mbps);
                      updateField("wifi_latency_ms", result.latency_ms);
                      updateField("wifi_speed_tier", result.speed_tier);
                      // Auto-update wifi_quality based on speed
                      if (result.download_mbps >= 100) {
                        updateField("wifi_quality", "very_fast");
                      } else if (result.download_mbps >= 50) {
                        updateField("wifi_quality", "fast");
                      } else if (result.download_mbps >= 20) {
                        updateField("wifi_quality", "good");
                      } else {
                        updateField("wifi_quality", "poor");
                      }
                      toast.success(`WiFi Speed: ${result.download_mbps} Mbps recorded!`);
                    }}
                  />
                  
                  {/* WiFi Quality - Manual fallback */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      WiFi Quality {!formData.wifi_speed_mbps && <span className="text-red-500">*</span>}
                      {formData.wifi_speed_mbps && <span className="text-xs text-green-600 ml-2">(Auto-set from speed test)</span>}
                    </label>
                    <div className="grid grid-cols-5 gap-2">
                      {wifiOptions.map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => updateField("wifi_quality", opt.value)}
                          className={`p-2.5 rounded-lg border-2 transition-all text-center ${
                            formData.wifi_quality === opt.value
                              ? "border-brand bg-brand/5"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <Wifi className={`w-5 h-5 mx-auto mb-1 ${
                            formData.wifi_quality === opt.value ? "text-brand" : "text-gray-400"
                          }`} />
                          <span className={`text-xs font-medium ${
                            formData.wifi_quality === opt.value ? "text-brand" : "text-gray-600"
                          }`}>{opt.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Noise Level */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Noise Level <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {noiseOptions.map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => updateField("noise_level", opt.value)}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            formData.noise_level === opt.value
                              ? "border-brand bg-brand/5"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <Volume2 className={`w-5 h-5 mx-auto mb-1 ${
                            formData.noise_level === opt.value ? "text-brand" : "text-gray-400"
                          }`} />
                          <span className={`text-sm font-medium block ${
                            formData.noise_level === opt.value ? "text-brand" : "text-gray-700"
                          }`}>{opt.label}</span>
                          <span className="text-xs text-gray-500">{opt.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Power Outlets */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Power Outlets
                    </label>
                    <div className="flex items-center gap-3">
                      <Plug className="w-5 h-5 text-gray-400" />
                      <input
                        type="range"
                        min="0"
                        max="20"
                        value={formData.power_outlets}
                        onChange={(e) => updateField("power_outlets", parseInt(e.target.value))}
                        className="flex-1 accent-brand"
                      />
                      <span className="text-sm font-medium text-gray-700 w-12 text-center">
                        {formData.power_outlets === 20 ? "20+" : formData.power_outlets}
                      </span>
                    </div>
                    {/* Exact socket count input */}
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-xs text-gray-500">Know the exact count?</span>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        placeholder="e.g. 12"
                        value={formData.plug_sockets_count || ""}
                        onChange={(e) => updateField("plug_sockets_count", e.target.value ? parseInt(e.target.value) : null)}
                        className="w-20 px-2 py-1 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand/50"
                      />
                    </div>
                  </div>
                  
                  {/* Food & Drinks */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Available
                    </label>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => updateField("has_food", !formData.has_food)}
                        className={`flex-1 p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                          formData.has_food ? "border-brand bg-brand/5 text-brand" : "border-gray-200 text-gray-600"
                        }`}
                      >
                        <UtensilsCrossed className="w-5 h-5" />
                        <span className="font-medium">Food</span>
                        {formData.has_food && <Check className="w-4 h-4" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => updateField("has_drinks", !formData.has_drinks)}
                        className={`flex-1 p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                          formData.has_drinks ? "border-brand bg-brand/5 text-brand" : "border-gray-200 text-gray-600"
                        }`}
                      >
                        <Coffee className="w-5 h-5" />
                        <span className="font-medium">Drinks</span>
                        {formData.has_drinks && <Check className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => updateField("description", e.target.value)}
                      placeholder="What makes this place great for working? Any tips for fellow remote workers?"
                      rows={3}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand resize-none"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 3: Your Details */}
            {currentStep === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-6"
              >
                <h2 className="text-xl font-bold text-gray-900 mb-1">Your Details</h2>
                <p className="text-sm text-gray-500 mb-4">We'll notify you when your submission is approved</p>
                
                {/* Points Banner - Show when logged in */}
                {contributor && (
                  <div className="mb-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                        <Trophy className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-amber-800">Earn 25 points for this venue!</p>
                        <p className="text-sm text-amber-600">You're signed in as {contributor.name || contributor.email}. Points will be added when approved.</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Sign In Section - Show when NOT logged in */}
                {!contributor && (
                  <div className="mb-6 p-4 bg-brand/5 border border-brand/20 rounded-xl">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-10 h-10 bg-brand/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Trophy className="w-5 h-5 text-brand" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Sign in to earn 25 points!</p>
                        <p className="text-sm text-gray-600">Contributors earn points for every venue they add. Sign in to track your progress and unlock badges.</p>
                      </div>
                    </div>
                    
                    {!showEmailLogin ? (
                      <div className="space-y-3">
                        {/* Google Sign In */}
                        <button
                          type="button"
                          onClick={handleGoogleLogin}
                          disabled={authLoading}
                          className="w-full py-3 bg-white border-2 border-gray-200 rounded-xl font-medium hover:bg-gray-50 hover:border-gray-300 transition-all flex items-center justify-center gap-3 disabled:opacity-50"
                          data-testid="google-signin-btn"
                        >
                          {authLoading ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <>
                              <svg className="w-5 h-5" viewBox="0 0 24 24">
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                              </svg>
                              Continue with Google
                            </>
                          )}
                        </button>
                        
                        {/* Email Login Link */}
                        <button
                          type="button"
                          onClick={() => setShowEmailLogin(true)}
                          className="w-full py-2 text-sm text-gray-600 hover:text-brand"
                        >
                          Or sign in with email
                        </button>
                      </div>
                    ) : (
                      <form onSubmit={handleEmailLogin} className="space-y-3">
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="email"
                            value={loginEmail}
                            onChange={(e) => setLoginEmail(e.target.value)}
                            placeholder="Your email"
                            className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                          />
                        </div>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="password"
                            value={loginPassword}
                            onChange={(e) => setLoginPassword(e.target.value)}
                            placeholder="Password"
                            className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                          />
                        </div>
                        {authError && (
                          <p className="text-sm text-red-600">{authError}</p>
                        )}
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => { setShowEmailLogin(false); setAuthError(""); }}
                            className="flex-1 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={authLoading}
                            className="flex-1 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            {authLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
                            Sign In
                          </button>
                        </div>
                        <p className="text-xs text-center text-gray-500">
                          Don't have an account? <a href="/contributor" className="text-brand hover:underline">Join the community</a>
                        </p>
                      </form>
                    )}
                    
                    <p className="text-xs text-gray-500 mt-3 text-center">
                      Or continue without signing in below
                    </p>
                  </div>
                )}
                
                <div className="space-y-5">
                  {/* Logged In User Info - Show confirmation after Google Auth */}
                  {contributor && (
                    <div className="p-4 bg-gradient-to-r from-teal-50 to-green-50 border border-teal-200 rounded-xl">
                      <div className="flex items-center gap-2 text-teal-700 mb-3">
                        <Check className="w-5 h-5" />
                        <span className="font-medium">Signed in successfully!</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {contributor.avatar_url || contributor.profile_picture ? (
                            <img 
                              src={contributor.avatar_url || contributor.profile_picture} 
                              alt={contributor.name}
                              className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-sm">
                              {contributor.name?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-gray-900">{contributor.name}</p>
                            <p className="text-sm text-gray-600">{contributor.email}</p>
                            {contributor.points > 0 && (
                              <p className="text-xs text-teal-600 flex items-center gap-1 mt-0.5">
                                <Trophy className="w-3 h-3" />
                                {contributor.points} points
                              </p>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={handleLogout}
                          className="text-sm text-gray-500 hover:text-red-600 px-3 py-1.5 rounded-lg hover:bg-white/50 transition-colors"
                        >
                          Sign out
                        </button>
                      </div>
                      <p className="text-xs text-teal-700 mt-3">
                        Your contribution will be linked to your profile. You'll earn 25 points when this venue is approved!
                      </p>
                    </div>
                  )}
                  
                  {/* Name - Show only if not logged in */}
                  {!contributor && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Your Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.submitter_name}
                        onChange={(e) => updateField("submitter_name", e.target.value)}
                        placeholder="John Smith"
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                      />
                    </div>
                  )}
                  
                  {/* Email - Show only if not logged in */}
                  {!contributor && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Your Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={formData.submitter_email}
                        onChange={(e) => updateField("submitter_email", e.target.value)}
                        placeholder="john@example.com"
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                      />
                    </div>
                  )}
                  
                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Additional Notes
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => updateField("notes", e.target.value)}
                      placeholder="Any other info we should know?"
                      rows={2}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand resize-none"
                    />
                  </div>
                  
                  {/* Terms & Marketing */}
                  <div className="space-y-3 pt-2">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.agreed_to_terms}
                        onChange={(e) => updateField("agreed_to_terms", e.target.checked)}
                        className="mt-1 rounded border-gray-300 text-brand focus:ring-brand"
                      />
                      <span className="text-sm text-gray-600">
                        I agree to the{" "}
                        <a href="/policies" target="_blank" className="text-brand hover:underline">
                          Terms & Conditions
                        </a>{" "}
                        <span className="text-red-500">*</span>
                      </span>
                    </label>
                    
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.marketing_opt_out}
                        onChange={(e) => updateField("marketing_opt_out", e.target.checked)}
                        className="mt-1 rounded border-gray-300 text-brand focus:ring-brand"
                      />
                      <span className="text-sm text-gray-600">
                        I do not wish to receive marketing communications
                      </span>
                    </label>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 4: Extras (Optional) */}
            {currentStep === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-6"
              >
                <h2 className="text-xl font-bold text-gray-900 mb-1">Extra Details</h2>
                <p className="text-sm text-gray-500 mb-6">These are optional but help others find the perfect spot</p>
                
                <div className="space-y-6">
                  {/* Neighborhood & Hours */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1.5">
                        Neighborhood
                      </label>
                      <input
                        type="text"
                        value={formData.neighborhood}
                        onChange={(e) => updateField("neighborhood", e.target.value)}
                        placeholder="e.g., Shoreditch"
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1.5">
                        Opening Hours
                      </label>
                      <input
                        type="text"
                        value={formData.opening_hours}
                        onChange={(e) => updateField("opening_hours", e.target.value)}
                        placeholder="e.g., 8am-6pm"
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
                      />
                    </div>
                  </div>
                  
                  {/* Pricing */}
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-2">
                      Pricing (approximate)
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-2">
                        <Coffee className="w-4 h-4 text-amber-600 flex-shrink-0" />
                        <PriceRating
                          label="Coffee"
                          value={formData.coffee_avg}
                          onChange={(val) => updateField("coffee_avg", val)}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <UtensilsCrossed className="w-4 h-4 text-orange-600 flex-shrink-0" />
                        <PriceRating
                          label="Food"
                          value={formData.food_avg}
                          onChange={(val) => updateField("food_avg", val)}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Photo Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-2">
                      <Camera className="w-4 h-4 inline mr-1.5" />
                      Photos (up to 3)
                    </label>
                    <p className="text-xs text-gray-400 mb-3">Help others see what the space looks like • Auto-compressed for fast loading</p>
                    
                    {/* Uploaded Images */}
                    {uploadedImages.length > 0 && (
                      <div className="grid grid-cols-3 gap-3 mb-3">
                        {uploadedImages.map((img, index) => (
                          <div key={img.name || `upload-${index}`} className="relative aspect-[4/3] rounded-lg overflow-hidden group">
                            <img
                              src={img.preview}
                              alt={`Upload ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute top-1.5 right-1.5 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                            {index === 0 && (
                              <span className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 bg-black/60 text-white text-xs rounded">
                                Main
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Upload Buttons - Camera & Gallery */}
                    {uploadedImages.length < 3 && (
                      <div className="grid grid-cols-2 gap-3">
                        {/* Camera Button (mobile) */}
                        <label className={`aspect-[4/3] rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-colors ${
                          uploadingImage ? 'border-brand bg-brand/5' : 'border-gray-200 hover:border-brand hover:bg-gray-50'
                        }`}>
                          {uploadingImage ? (
                            <Loader2 className="w-6 h-6 text-brand animate-spin" />
                          ) : (
                            <>
                              <Camera className="w-8 h-8 text-gray-400 mb-2" />
                              <span className="text-sm font-medium text-gray-600">Take Photo</span>
                              <span className="text-xs text-gray-400">Use camera</span>
                            </>
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            onChange={handleImageUpload}
                            className="hidden"
                            data-testid="photo-camera-input"
                          />
                        </label>
                        
                        {/* Gallery Button */}
                        <label className={`aspect-[4/3] rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-colors ${
                          uploadingImage ? 'border-brand bg-brand/5' : 'border-gray-200 hover:border-brand hover:bg-gray-50'
                        }`}>
                          {uploadingImage ? (
                            <Loader2 className="w-6 h-6 text-brand animate-spin" />
                          ) : (
                            <>
                              <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
                              <span className="text-sm font-medium text-gray-600">Choose Photo</span>
                              <span className="text-xs text-gray-400">From gallery</span>
                            </>
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImageUpload}
                            className="hidden"
                            data-testid="photo-gallery-input"
                          />
                        </label>
                      </div>
                    )}
                    
                    {uploadedImages.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <p className="text-xs text-gray-400">
                          {uploadedImages.length}/3 photos • First photo will be the main image
                        </p>
                        {uploadedImages.some(img => img.originalSize && img.compressedSize) && (
                          <p className="text-xs text-green-600">
                            ✓ Images auto-compressed for faster loading
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Work-Friendly Features */}
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-3">
                      Work-Friendly Features
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {workFeatures.map(feature => {
                        const Icon = feature.icon;
                        const isSelected = formData[feature.key];
                        return (
                          <button
                            key={feature.key}
                            type="button"
                            onClick={() => updateField(feature.key, !isSelected)}
                            className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2 ${
                              isSelected
                                ? "border-brand bg-brand/5 text-brand"
                                : "border-gray-200 hover:border-gray-300 text-gray-600"
                            }`}
                          >
                            <Icon className="w-4 h-4" />
                            <span className="text-sm font-medium">{feature.label}</span>
                          </button>
                        );
                      })}
                    </div>
                    
                    {/* Printing Cost Fields - Show when printing is selected */}
                    {formData.has_printing && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <p className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                          <Printer className="w-4 h-4" />
                          Printing Costs (per A4 sheet)
                        </p>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1.5">
                              Black & White
                            </label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">£</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={formData.printing_cost_bw}
                                onChange={(e) => updateField("printing_cost_bw", e.target.value)}
                                placeholder="0.10"
                                className="w-full pl-7 pr-3 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand text-sm"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1.5">
                              Colour
                            </label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">£</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={formData.printing_cost_color}
                                onChange={(e) => updateField("printing_cost_color", e.target.value)}
                                placeholder="0.25"
                                className="w-full pl-7 pr-3 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand text-sm"
                              />
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </div>
                  
                  {/* Leave a Review (Contributor Only) */}
                  <div className="border-t border-gray-100 pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Leave a Review
                          </label>
                          {contributor && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex items-center gap-1">
                              <Star className="w-3 h-3" /> +10 bonus points
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">Share your experience working here</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => updateField("include_review", !formData.include_review)}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          formData.include_review ? "bg-brand" : "bg-gray-200"
                        }`}
                        data-testid="include-review-toggle"
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                            formData.include_review ? "translate-x-6" : ""
                          }`}
                        />
                      </button>
                    </div>
                    
                    {formData.include_review && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="space-y-4 bg-gray-50 rounded-lg p-4"
                      >
                        {/* Rating */}
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-2">
                            Overall Rating: {formData.review_rating}/10
                          </label>
                          <input
                            type="range"
                            min="1"
                            max="10"
                            value={formData.review_rating}
                            onChange={(e) => updateField("review_rating", parseInt(e.target.value))}
                            className="w-full accent-brand"
                            data-testid="review-rating-slider"
                          />
                          <div className="flex justify-between text-xs text-gray-400 mt-1">
                            <span>Poor</span>
                            <span>Excellent</span>
                          </div>
                        </div>
                        
                        {/* Comment */}
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-2">
                            Your Review
                          </label>
                          <textarea
                            value={formData.review_comment}
                            onChange={(e) => updateField("review_comment", e.target.value)}
                            placeholder="What was your experience like? WiFi speed, noise levels, seating comfort..."
                            rows={3}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand text-sm"
                            data-testid="review-comment"
                          />
                        </div>
                        
                        <p className="text-xs text-gray-500">
                          Your review will be published along with the venue once approved.
                        </p>
                      </motion.div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
            {currentStep > 1 ? (
              <button
                onClick={prevStep}
                className="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:text-gray-900"
              >
                <ChevronLeft className="w-5 h-5" />
                Back
              </button>
            ) : (
              <div />
            )}
            
            {currentStep < 4 ? (
              <button
                onClick={nextStep}
                className="flex items-center gap-2 px-6 py-2.5 bg-brand text-white rounded-lg hover:bg-brand-dark"
              >
                {currentStep === 3 ? "Add Extras" : "Continue"}
                <ChevronRight className="w-5 h-5" />
              </button>
            ) : (
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    if (validateStep(3)) handleSubmit();
                  }}
                  disabled={submitting}
                  className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Skip & Submit
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex items-center gap-2 px-6 py-2.5 bg-brand text-white rounded-lg hover:bg-brand-dark disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5" />
                      Submit Location
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Step Indicator Text */}
        <p className="text-center text-sm text-gray-500 mt-4">
          Step {currentStep} of 4 {currentStep === 4 && "(Optional)"}
        </p>
      </main>
      
      <Footer />
    </div>
  );
}
